import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

/**
 * Generic real-time subscription for an admin-only Firestore collection.
 * Access is enforced by Firestore rules (isAdmin()), so these can write directly
 * from the client — no Cloud Functions required.
 */
export function useAdminCollection<T extends { id: string }>(
  name: string,
  orderField: string = 'updatedAt',
) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const q = query(collection(db, name), orderBy(orderField, 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as T[]);
        setLoading(false);
      },
      (err) => {
        console.error(`Error fetching ${name}:`, err);
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [name, orderField]);

  return { items, loading, error };
}

export async function createAdminDoc(name: string, data: Record<string, unknown>, createdBy: string) {
  return addDoc(collection(db, name), {
    ...data,
    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateAdminDoc(name: string, id: string, data: Record<string, unknown>) {
  return updateDoc(doc(db, name, id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteAdminDoc(name: string, id: string) {
  return deleteDoc(doc(db, name, id));
}
